import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2020 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as AbReactResources from "abreact/generated/locale/AbReactResources";
import { DEFAULT_DEBOUNCE_DELAY } from "abreact/incubator/ui/component/AbDropdownMachine";
import { defaultItemDisplayStringFunction } from "abreact/incubator/ui/form/AbInputUtils";
import { SelectionMode } from "abreact/incubator/ui/manager/SelectionManager";
import { AbPopup } from "abreact/incubator/ui/popup/AbPopup";
import { PopupUtils } from "abreact/incubator/ui/popup/PopupUtils";
import { CssUtils } from "abreact/incubator/ui/utils/CssUtils";
import { FocusUtils } from "abreact/incubator/ui/utils/FocusUtils";
import { ReactUtils } from "abreact/incubator/ui/utils/ReactUtils";
import { AbList } from "abreact/input/AbList";
import { AbQueryThrottleImpl } from "abreact/input/AbQueryThrottle";
import { AbTextInput } from "abreact/input/AbTextInput";
import { AbButton } from "abreact/misc/AbButton";
import { AbChiclet } from "abreact/misc/AbChiclet";
import { AbSize, AbSizeContext, sizeProviderWrapper } from "abreact/misc/AbSize";
import { AbEnriched } from "abreact/utils/enrichment/AbEnriched";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { CancelablePromise, PromiseCanceled } from "abweb/utils/PromiseUtils";
import { StringUtils } from "abweb/utils/StringUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import * as React from "react";
import * as ReactDOM from "react-dom";
let __autocompleteQueryThrottleImpl = undefined;
const autocompleteQueryThrottle = () => {
  if (!__autocompleteQueryThrottleImpl) {
    __autocompleteQueryThrottleImpl = new AbQueryThrottleImpl();
  }
  return __autocompleteQueryThrottleImpl;
};

/**
 * The properties that can be specified when constructing an auto-complete box.
 */

/**
 * A React-based implementation of an auto-complete component that offers values choices as you type
 */
export class AbAutoComplete extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "defaultInputId", TypeUtils.generateUniqueId("ab-auto-complete-input"));
    _defineProperty(this, "valuePickerPromise", void 0);
    _defineProperty(this, "suggestionsPromise", void 0);
    _defineProperty(this, "refreshItemsTimeout", void 0);
    _defineProperty(this, "dropdownElement", null);
    _defineProperty(this, "inputRef", /*#__PURE__*/React.createRef());
    _defineProperty(this, "valueElement", /*#__PURE__*/React.createRef());
    /**
     * The renderer used when is no value renderer or item renderer is passed in.
     * Assume we can use the display string renderer.
     * @param item contains the item and attributes with which to render the item.
     */
    _defineProperty(this, "defaultValueTokenRenderer", item => {
      let classes = StringUtils.cssClassStr("ab-auto-complete-value", this.props.disabled && "disabled");
      if (this.props.valueRenderer) {
        return this.props.valueRenderer(item);
      }
      if (this.props.itemRenderer) {
        return this.props.itemRenderer(item);
      }
      return /*#__PURE__*/React.createElement("span", {
        className: classes
      }, this.getItemDisplayName(item));
    });
    _defineProperty(this, "getItemDisplayName", item => {
      const itemInfo = this.props.itemInfoFunction?.(item);
      return itemInfo?.displayName || (this.props.itemDisplayStringFunction ? this.props.itemDisplayStringFunction(item) : undefined) || defaultItemDisplayStringFunction(item);
    });
    /**
     * Event Handling
     */
    /**
     * Invoke the provided picker function and receive a new value for this autocomplete box
     */
    _defineProperty(this, "onPickValue", () => {
      if (this.props.valuePickerFunction) {
        // Using a cancelable promise so that it can be canceled if this component is unmounted prior to its resolution.
        // Using .then promise handling here to terminate the Promise return value here so we can return void.
        // We are explicitly letting exceptions bubble up to our caller here, they provided the picker function.
        // This Promise may not reject, if it does it will throw
        this.valuePickerPromise = new CancelablePromise(this.props.valuePickerFunction(this.props.value));
        this.valuePickerPromise.promise.then(selectedValue => {
          // If there was no change then do not alert a change
          if (!this.isEqualValue(this.props.value, selectedValue)) {
            this.props.onChange(selectedValue);
          }
        }).catch(reason => {
          return null;
        });
      }
    });
    /**
     * This function handles changes in the input box text
     */
    _defineProperty(this, "onTextChange", text => {
      if (text !== this.state.currentText) {
        // set the machine current text to the value of the input control
        if (text === "") {
          this.setState({
            currentText: "",
            suggestions: [],
            showingPopup: false
          });
        } else {
          // Ensure the currentText update has completed before the refresh
          this.setState({
            currentText: text,
            suggestions: [],
            showingPopup: false
          }, () => this.refreshItems());
        }
      }
    });
    /**
     * Handle keyboard character events from the autocomplete input control
     * @param event
     */
    _defineProperty(this, "onInputKeyDown", event => {
      const popupAcceptsFocus = Boolean(this.props.popupProperties && this.props.popupProperties.focus);
      if (event.key === KeyValue.TAB && !popupAcceptsFocus) {
        this.reset();
        // Note: No prevent default so the user can tab out of the dropdown
      } else if (event.key === KeyValue.ESCAPE) {
        this.reset();
        event.stopPropagation();
        event.preventDefault();
        return true;
      } else if (event.key === KeyValue.ENTER) {
        // Chose the currently selected item or items as the value
        if (this.state.showingPopup && this.state.suggestions[this.state.selection]) {
          this.addValue(this.state.suggestions[this.state.selection]);
        }
        event.stopPropagation();
        event.preventDefault();
        return true;
      } else if (event.key === KeyValue.ARROW_UP || event.key === KeyValue.ARROW_DOWN) {
        if (this.state.showingPopup) {
          event.key === KeyValue.ARROW_UP ? this.selectPreviousItem() : this.selectNextItem();
        }
        event.stopPropagation();
        event.preventDefault();
        return true;
      }
      return false;
    });
    this.state = {
      currentText: "",
      showingPopup: false,
      suggestions: [],
      selection: 0
    };

    /*
     * This needed to be punted because I could not think of a way to check the value type for type of string or string[]
     * Maybe need to think of a way to make it not compile.
     */
    if (!this.isSingleSelect && !Array.isArray(this.props.value)) {
      throw new Error(`Improper use of AbAutoComplete: The value of a multi select control should be an array but is ${this.props.value}`);
    }
    if (this.isSingleSelect && Array.isArray(this.props.value)) {
      throw new Error(`Improper use of AbAutoComplete: The value of a single select control must be an item or undefined, but is ${this.props.value}`);
    }
  }

  /**
   * Lifecycle Methods
   */
  componentWillUnmount() {
    // Cancel any outstanding updates when being unmounted.
    this.suggestionsPromise && this.suggestionsPromise.cancel();
    this.valuePickerPromise && this.valuePickerPromise.cancel();
  }

  /**
   * Rendering Section
   */
  render() {
    const {
      valuePickerFunction
    } = this.props;
    const rootClasses = [this.isSingleSelect && "ab-single-line", valuePickerFunction && "ab-object-picker", this.props.size ?? this.context ?? AbSize.MEDIUM];
    const mainClasses = StringUtils.cssClassStr("ab-auto-complete-main", this.isSingleSelect ? "single-selection" : "multiple-selection");
    const inner = /*#__PURE__*/React.createElement("div", _extends({}, ReactUtils.inputProps("ab-auto-complete", this.props, rootClasses), {
      ref: this.registerDropdownElement.bind(this),
      "aria-disabled": this.props.disabled,
      tabIndex: -1
    }), /*#__PURE__*/React.createElement("div", {
      className: mainClasses,
      onClick: () => this.onClick
    }, this.renderValues(), this.renderInput()), this.props.valuePickerFunction && this.renderPickerButton(), this.state.showingPopup && this.renderPopup());
    return sizeProviderWrapper(inner, this.props, this.context);
  }
  renderInput() {
    const {
      placeholder,
      disableTextInput,
      tooltip
    } = this.props;
    const {
      id,
      disabled,
      ...inputProps
    } = ReactUtils.inputProps("ab-auto-complete-input", this.props, ["abui"]);
    if (this.isSingleSelect && this.props.value) {
      return null;
    }
    let value = "";
    if (this.state.currentText) {
      value = this.state.currentText;
    } else if (this.props.disableTextInput && this.props.value === undefined) {
      value = AbReactResources.abAutoComplete_searchNotAvailable();
    }
    return /*#__PURE__*/React.createElement(AbTextInput, _extends({}, inputProps, {
      id: id ? `${id}-input` : this.defaultInputId,
      disabled: disabled || disableTextInput,
      tooltip: tooltip,
      value: value,
      placeholder: placeholder,
      onChange: this.onTextChange,
      abRef: this.inputRef,
      __htmlProps: {
        autoComplete: "off",
        "aria-autocomplete": "list",
        "aria-expanded": this.state.suggestions.length > 0,
        onKeyDown: this.onInputKeyDown
      }
    }));
  }

  /**
   * Renders the value tokens inside the autocomplete box area
   */
  renderValues() {
    if (this.props.value === undefined) {
      return null;
    }
    if (Array.isArray(this.props.value)) {
      return this.props.value.map(value => {
        const itemInfo = this.props.itemInfoFunction && this.props.itemInfoFunction(value);
        const canRemove = !this.props.disabled && itemInfo?.canRemove !== false;
        const key = itemInfo?.id || this.getItemDisplayName(value);
        return /*#__PURE__*/React.createElement(AbChiclet, {
          key: key,
          label: this.defaultValueTokenRenderer(value),
          onClose: canRemove ? () => this.removeValue(value) : undefined,
          tooltip: canRemove ? undefined : itemInfo?.cannotRemoveReason
        });
      });
    } else {
      const value = this.props.value;
      const itemInfo = this.props.itemInfoFunction && this.props.itemInfoFunction(value);
      const canRemove = !this.props.disabled && itemInfo?.canRemove !== false;
      return /*#__PURE__*/React.createElement(AbChiclet, {
        ref: this.valueElement,
        label: this.defaultValueTokenRenderer(value),
        onClose: canRemove ? () => this.removeValue(value) : undefined,
        tooltip: canRemove ? undefined : itemInfo?.cannotRemoveReason
      });
    }
  }

  /**
   * We support opening a picker dialog to choose the item for the input
   */
  renderPickerButton() {
    return /*#__PURE__*/React.createElement(AbButton, {
      className: "ab-value-picker-button ab-square-button",
      disabled: this.props.disabled,
      onClick: this.onPickValue,
      label: "..."
    });
  }

  /**
   * Draw listbox and selectable items into the popup.
   * Note: There is no click handler on AbList. We handle click in the itemRenderer and also the onChange in case the
   * List somehow gets focus even though it should not.
   */
  renderPopupList() {
    return /*#__PURE__*/React.createElement(AbList, {
      className: "ab-auto-complete-suggestions-list ab-list-no-overflow",
      items: this.state.suggestions.map((item, index) => AbEnriched.create(item, {
        className: CssUtils.composeClassNames("ab-auto-complete-list-item", index === this.state.selection && "ab-selected")
      })),
      tabIndex: -1,
      value: undefined,
      selectionMode: SelectionMode.SINGLE,
      disabled: this.props.disabled,
      itemRenderer: this.props.itemRenderer ?? this.getItemDisplayName,
      onChange: item => {
        this.addValue(item);
      },
      keyFunction: this.props.keyFunction
    });
  }
  renderPopup() {
    const classes = ReactUtils.classNamesString(["ab-dropdown-popup ab-auto-complete-popup", this.props.popupProperties && this.props.popupProperties.className]);

    // TS bug spreading generics so the cast is needed
    let popupProperties = {
      ...this.props.popupProperties,
      className: classes
    };
    return /*#__PURE__*/React.createElement(AbPopup, _extends({}, popupProperties, {
      flushContent: true,
      position: this.props.popupPosition,
      positioner: this.popupPositioner.bind(this),
      origin: this.dropdownElement || undefined,
      onKeyDown: this.onInputKeyDown,
      overlay: false,
      onBackgroundCaptureMouseDown: event => this.onBackgroundCaptureMouseDown(event),
      onClose: () => this.setState({
        showingPopup: false
      })
    }), /*#__PURE__*/React.createElement("div", {
      className: "ab-dropdown-content",
      "aria-live": "polite"
    }, this.renderPopupList()));
  }
  onClick() {
    FocusUtils.setFocus(this.inputRef.current?.inputElement);
  }

  /**********************************************
   * AutoComplete Machine type functions
   */

  get isSingleSelect() {
    return this.props.selectionMode === undefined || this.props.selectionMode === SelectionMode.SINGLE;
  }

  /**
   * Takes a group of values and concats them to the current Values if multiselect.
   * Or it replaces the reset the current value to value.
   * @param value is the new value or values that are being added to the list of values
   */
  addValue(value) {
    if (this.isSingleSelect) {
      // Selection Mode Single so it should be an ItemT or undefined.
      // Check for array which should not be allowed.  Thanks ValuePicker...
      this.chooseValue(Array.isArray(value) ? value[0] : value);
    } else if (value !== undefined) {
      this.chooseValue(this.props.value.concat(value));
    }
  }

  /**
   * Invoked when the user selects a value for the component via dropdown or picker.
   * Set value to the new value and set the focus to the INPUT
   * Reset the dropdown suggestions and show status
   */
  chooseValue(value) {
    // Clear out everything to do with the popup
    this.reset();
    // Alert the owner that the value changed
    this.props.onChange(value);
    // set focus to the input control only when it exists
    // ALERT! Single selection controls with a value there will be no INPUT control so the focus will not be set
    window.requestAnimationFrame(() => {
      // When re-rendering the focused component is lost, set the focus later
      if (this.isSingleSelect && this.props.value !== undefined) {
        // when value element is rendered it has the focus
        this.valueElement.current && this.valueElement.current.focus();
      } else {
        FocusUtils.setFocus(this.inputRef.current?.inputElement);
      }
    });
  }

  /**
   * Invoke when the user removes a value from the component.
   */
  removeValue(value) {
    let newValues;
    if (Array.isArray(this.props.value)) {
      // If Array remove all instances of that value
      newValues = this.props.value.filter(i => !this.areItemsEqual(i, value));
      this.chooseValue(newValues);
    } else if (this.props.value !== undefined && this.areItemsEqual(value, this.props.value)) {
      // If not array and this is my value
      this.chooseValue(undefined);
    }
  }

  /**
   * Test if item exists in the array of current values
   * @param item
   */
  hasValue(item) {
    if (!Array.isArray(this.props.value)) {
      return item === this.props.value;
    }
    return item === undefined && this.props.value.length === 0 || this.props.value.some(valueItem => this.areItemsEqual(valueItem, item));
  }
  areItemsEqual(item1, item2) {
    if (item1 == null || item2 == null) {
      return item1 === item2;
    } else if (this.props.itemInfoFunction) {
      const info1 = this.props.itemInfoFunction(item1);
      if (info1.isEqual) {
        return info1.isEqual(item2);
      } else {
        const info2 = this.props.itemInfoFunction(item2);
        return info2.isEqual ? info2.isEqual(item1) : info1.id === info2.id;
      }
    } else {
      return this.props.valuesEqual ? this.props.valuesEqual(item1, item2) : item1 === item2;
    }
  }

  /**
   * This function shallow compares the value or value array elements with === to determine if it is the same.
   */
  isEqualValue(value1, value2) {
    if (value1 == null || value2 == null) {
      return value1 === value2;
    }
    if (Array.isArray(value1) && Array.isArray(value2)) {
      return value1.length === value2.length && value1.every((value, index) => this.areItemsEqual(value, value2[index]));
    }
    return this.areItemsEqual(value1, value2);
  }
  selectNextItem() {
    let index = Math.min(this.state.selection + 1, this.state.suggestions.length - 1);
    this.setState({
      selection: index
    });
  }
  selectPreviousItem() {
    let index = Math.max(this.state.selection - 1, 0);
    this.setState({
      selection: index
    });
  }

  /**
   * Refreshes the items shown by the dropdown.
   */
  refreshItems() {
    if (!this.props.suggestionsFunction) {
      return;
    }
    if (this.refreshItemsTimeout !== undefined) {
      clearTimeout(this.refreshItemsTimeout);
      this.refreshItemsTimeout = undefined;
    }
    if (this.suggestionsPromise) {
      this.suggestionsPromise.cancel();
      this.suggestionsPromise = undefined;
    }
    this.refreshItemsTimeout = setTimeout(() => {
      if (!this.suggestionsPromise && this.props.suggestionsFunction) {
        autocompleteQueryThrottle().fireQueryIfSlotOpenOrWaitForSlot(() => {
          this.suggestionsPromise = new CancelablePromise(this.props.suggestionsFunction(this.state.currentText));
          return this.suggestionsPromise.promise;
        }, items => {
          // Double check the currentText here because the cancelable promises do not seem to be working
          let list = this.state.currentText === "" || !Array.isArray(items) ? [] : items;
          list = list.filter(item => !this.hasValue(item)); // should be part of suggestions function
          this.setState({
            suggestions: list,
            showingPopup: list.length > 0,
            selection: 0
          });
        }, error => {
          if (!ClassUtils.isInstanceOf(error, PromiseCanceled)) {
            throw new Error("Failed to fetch items for dropdown: " + error);
          }
        });
      }
    }, this.props.debounceDelayMillis ?? DEFAULT_DEBOUNCE_DELAY);
  }

  /**
   * This function waits a frame so that clearing selection and setting text does not interfere with selecting the
   * new value.  It is called after a new value is chosen.  It's a bit of a hack so it should be removed...
   */
  reset() {
    this.setState({
      currentText: "",
      suggestions: [],
      showingPopup: false,
      selection: 0
    });
  }
  registerDropdownElement(dropdownElement) {
    this.dropdownElement = dropdownElement;
  }
  onBackgroundCaptureMouseDown(event) {
    // Only close the popup if the click is in the background, not on the dropdown itself
    const target = event.target;
    const thisElement = ReactDOM.findDOMNode(this);
    if (!thisElement.contains(target)) {
      this.reset();
    }
  }
  popupPositioner(props) {
    const popupPositioner = PopupUtils.defaultPositioner(this.props.popupPosition) || PopupUtils.VERTICAL_POSITIONER;
    return popupPositioner(props);
  }
}
_defineProperty(AbAutoComplete, "contextType", AbSizeContext);
//# sourceMappingURL=AbAutoComplete.js.map
